import { inject } from '@angular/core';
import {
  provideModuleWidgets,
  SHELL_SEARCH_ACTION,
  ShellSearchAction,
  SOFTLINE_WIDGET_OUTLET_DASHBOARD,
} from '@softapps/core';
import { EventServiceRepository } from '@softapps/core/events';
import {
  RosterContent,
  RosterEventRepository,
  RosterFeature,
  RosterResourceRepository,
  RosterService,
  RosterStructureRepository,
} from '@softapps/pms/roster';
import { getModuleGroupById } from '@softline/application';
import { default as de } from '../i18n/de.json';
import { default as en } from '../i18n/en.json';
import { SoftlineMenuBarComponent } from './components/menu-bar/menu-bar.component';
import { EventServiceEventProvider } from './providers/event-service.event-provider';
import { SoftlineRosterEventRepository } from './repositories/softline-roster-event.repository';
import { SoftlineRosterResourceRepository } from './repositories/softline-roster-resource.repository';
import { SoftlineRosterStructureRepository } from './repositories/softline-roster-structure.repository';
import { SoftlineRosterGlobalCreateService } from './services/softline-roster-global-create.service';
import { SoftlineRosterService } from './services/softline-roster.service';
import { provideRosterEventProvider } from './types/roster-event-provider';

export function withSoftlineTicketRoster(): RosterFeature<RosterContent> {
  return {
    name: 'softline-ticket-roster',
    module: {
      name: 'softline-ticket-roster',
      title: '#PMS_ROSTER.TITLE',
      icon: 'fa-regular fa-calendars',
      color: 'bg-primary text-white',
      permission: 'SOFTAPPS.SOFTLINE_TICKET_ROSTER',
      group: getModuleGroupById(8),
    },
    info: {
      library: '@softapps/softline/roster',
      description: 'Dienstplaner für Softline-Tickets und Arbeitspakete.',
    },
    providers: [
      { provide: RosterService, useClass: SoftlineRosterService },
      { provide: RosterEventRepository, useClass: SoftlineRosterEventRepository },
      { provide: RosterResourceRepository, useClass: SoftlineRosterResourceRepository },
      { provide: RosterStructureRepository, useClass: SoftlineRosterStructureRepository },
      EventServiceRepository,
      provideRosterEventProvider(EventServiceEventProvider),
    ],
    translations: { de, en },
    menuBar: SoftlineMenuBarComponent,
    components: {
      eventComponent: () =>
        import('./components/event/event.component').then(
          (o) => o.SoftlineTicketRosterEventComponent
        ),
      calendarResourceComponent: () =>
        import('./components/resource/resource.component').then(
          (o) => o.SoftlineTicketRosterResourceComponent
        ),
      resourceComponent: () =>
        import('./components/tasks-list/tasks-list.component').then(
          (o) => o.SoftlineTicketRosterTasksListComponent
        ),
    },
  };
}

export function provideSoftlineRosterSearchActions() {
  return {
    provide: SHELL_SEARCH_ACTION,
    multi: true,
    useFactory: (): ShellSearchAction => {
      const globalCreate = inject(SoftlineRosterGlobalCreateService);
      return {
        title: '#PMS_ROSTER.NEW_ROSTER_ENTRY',
        category: '#PMS_ROSTER.TITLE',
        icon: 'fa-regular fa-calendar-plus',
        permission: 'SOFTAPPS.SOFTLINE_TICKET_ROSTER',
        action: () => globalCreate.quickCreate(new Date()),
        url: '/roster/softline-ticket-roster',
      };
    },
  };
}

export function provideSoftlineTimelineWidget() {
  return provideModuleWidgets([
    {
      outletNames: [SOFTLINE_WIDGET_OUTLET_DASHBOARD],
      group: 'pms',
      loadComponent: () =>
        import('./widgets/timeline/timeline.component').then(
          (m) => m.SoftlineTimelineWidgetComponent
        ),
      permission: 'SOFTAPPS.SOFTLINE_TICKET_ROSTER',
    },
  ]);
}
